import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Track if we're doing initial auth validation (don't redirect during this)
let isValidatingToken = false;

export const setValidatingToken = (value) => {
  isValidatingToken = value;
};

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Skip redirect during initial token validation - let AuthContext handle it
      if (isValidatingToken) {
        return Promise.reject(error);
      }
      
      // Only clear tokens and redirect if we're not already on auth pages
      const currentPath = window.location.pathname;
      const authPaths = ['/login', '/register', '/'];
      
      // Don't redirect if already on auth pages
      if (!authPaths.includes(currentPath)) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Use replace to avoid adding to browser history
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Challenges API
export const challengesAPI = {
  getPackages: () => api.get('/challenges/packages'),
  getInstantPackages: () => api.get('/challenges/instant'),
  getAllPackages: () => api.get('/challenges/all'),
  getMyChallenges: () => api.get('/challenges/my'),
  getChallengeDetail: (id) => api.get(`/challenges/${id}`),
  requestRetake: (id) => api.post(`/challenges/${id}/retake`),
  simulateTrade: (id, profit) => api.post(`/challenges/${id}/trade`, null, { params: { profit } }),
  createDemo: () => api.post('/challenges/demo'),
};

// Payments API
export const paymentsAPI = {
  createCheckout: (packageId, originUrl, couponCode = null, upgradeProfitSplit = false, payoutOnDemand = false) => 
    api.post('/payments/checkout', { 
      package_id: packageId, 
      origin_url: originUrl, 
      coupon_code: couponCode,
      upgrade_profit_split: upgradeProfitSplit,
      payout_on_demand: payoutOnDemand
    }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Coupons API
export const couponsAPI = {
  validate: (code, packageId) => api.post(`/coupons/validate?code=${code}&package_id=${packageId}`),
};

// Crypto / KBTCOIN API
export const cryptoAPI = {
  joinWaitlist: (email) => api.post('/crypto/waitlist', { email }),
  getWaitlistCount: () => api.get('/crypto/waitlist/count'),
};

// Elite Funded Trader API
export const eliteFundedAPI = {
  checkEligibility: () => api.get('/elite-funded/eligibility'),
  apply: () => api.post('/elite-funded/apply'),
};

// Trade Sync API (for real-time monitoring)
export const tradeSyncAPI = {
  getFloatingLoss: (challengeId) => api.get(`/trade-sync/floating-loss/${challengeId}`),
  checkBreach: (challengeId) => api.post(`/trade-sync/check-breach/${challengeId}`),
  syncPositions: (challengeId) => api.post(`/trade-sync/sync/${challengeId}`),
  getStatus: () => api.get('/trade-sync/status'),
};

// Platform Settings API (Public - No Auth Required)
export const platformAPI = {
  getSettings: () => api.get('/platform/settings'),
  getMemberCount: () => api.get('/platform/member-count'),
};

// KYC API
export const kycAPI = {
  getStatus: () => api.get('/kyc/status'),
  submit: (data) => api.post('/kyc/submit', data),
  getDocuments: () => api.get('/kyc/documents'),
  uploadDocument: (documentType, file) => {
    const formData = new FormData();
    formData.append('document_type', documentType);
    formData.append('file', file);
    return api.post('/kyc/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Payouts API
export const payoutsAPI = {
  request: (data) => api.post('/payouts/request', data),
  getMyPayouts: () => api.get('/payouts/my'),
};

// Trading API
export const tradingAPI = {
  getPlatforms: () => api.get('/trading/platforms'),
  getSymbols: () => api.get('/trading/symbols'),
  getPrice: (symbol) => api.get(`/trading/price/${symbol}`),
  getAccount: (challengeId) => api.get(`/trading/account/${challengeId}`),
  getMetrics: (challengeId) => api.get(`/trading/metrics/${challengeId}`),
  getPositions: (challengeId) => api.get(`/trading/positions/${challengeId}`),
  getHistory: (challengeId) => api.get(`/trading/history/${challengeId}`),
  placeOrder: (challengeId, order) => api.post(`/trading/order/${challengeId}`, order),
  closePosition: (challengeId, positionId) => api.post(`/trading/close/${challengeId}/${positionId}`),
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getUsers: () => api.get('/admin/users'),
  getChallenges: () => api.get('/admin/challenges'),
  getPayouts: () => api.get('/admin/payouts'),
  updatePayoutStatus: (id, status) => api.patch(`/admin/payouts/${id}`, null, { params: { status } }),
  updateChallengeStatus: (id, status) => api.patch(`/admin/challenges/${id}/status`, null, { params: { status } }),
  // Coupons
  getCoupons: () => api.get('/admin/coupons'),
  createCoupon: (data) => api.post('/admin/coupons', data),
  updateCoupon: (id, isActive) => api.patch(`/admin/coupons/${id}`, null, { params: { is_active: isActive } }),
  deleteCoupon: (id) => api.delete(`/admin/coupons/${id}`),
  // KYC
  getPendingKYC: () => api.get('/admin/kyc/pending'),
  getUserKYC: (userId) => api.get(`/admin/kyc/${userId}`),
  verifyKYC: (userId, data) => api.patch(`/admin/kyc/${userId}/verify`, null, { params: data }),
  // Match-Trade Integration
  getMatchTradeStatus: () => api.get('/admin/matchtrade/status'),
  getMatchTradeOffers: () => api.get('/admin/matchtrade/offers'),
  getMatchTradePaymentGateways: () => api.get('/admin/matchtrade/payment-gateways'),
  createMatchTradeAccount: (userId, offerUuid) => api.post('/admin/matchtrade/create-account', null, { params: { user_id: userId, offer_uuid: offerUuid } }),
  matchTradeDeposit: (login, amount, comment) => api.post('/admin/matchtrade/deposit', null, { params: { login, amount, comment } }),
  getMatchTradePositions: (login) => api.get(`/admin/matchtrade/positions/${login}`),
  getMatchTradePropAccounts: () => api.get('/admin/matchtrade/prop-accounts'),
  // SMS Service
  getSMSStatus: () => api.get('/admin/sms/status'),
  testSMS: (phoneNumber) => api.post('/admin/sms/test', null, { params: { phone_number: phoneNumber } }),
};

// Technical Analysis API
export const technicalAnalysisAPI = {
  getInstruments: () => api.get('/technical-analysis/instruments'),
  getAnalysis: (symbol) => api.get(`/technical-analysis/${symbol}`),
  getAllSummary: () => api.get('/technical-analysis/summary/all'),
};

export default api;
